/* eslint-disable max-lines */
/* eslint-disable sort-keys */
import type {
    NinchatEnvironmentConfigQuestionnaireItemsRT,
    NinchatEnvironmentConfigThemeRT,
} from 'embed/types'
import {Route} from 'navigation/types'
import router from 'navigation/router'
import {z} from 'zod'

const InitialRouteSchema = z
    .custom<Route>()
    .refine((route) => router.validate(route))


const SSOSchema = z.union([
    z.object({
        email: z.string(),
        name: z.never(),
        type: z.literal('email'),
    }),
    z.object({
        email: z.never(),
        name: z.string(),
        type: z.never(),
    }),
])

const ActionIconSchema = z.optional(z.union([
    z.boolean(),
    z.object({
        src: z.optional(z.string()),
        svg: z.optional(z.string()),
        alt: z.optional(z.string()),
        width: z.optional(z.number()),
        height: z.optional(z.number()),
    }),
]))

export const QuestionnaireElementLogic = z.object({
    conditions: z.array(z.object({
        $value: z.optional(z.union([z.string(), z.number(), z.boolean()])),
        $and: z.array(z.record(z.string(), z.object({regexp: z.optional(z.string())}))),
        $or: z.array(z.record(z.string(), z.object({regexp: z.optional(z.string())}))),
        $nor: z.array(z.record(z.string(), z.object({regexp: z.optional(z.string())}))),
        $not: z.record(z.string(), z.object({regexp: z.optional(z.string())})),
        $sum: z.array(z.record(z.string(), z.object({regexp: z.optional(z.string())}))),
        $gt: z.number(), // greater than
        $gte: z.number(), // greater than or equal
        $lt: z.number(), // less than
        $lte: z.number(), // less than or equal

    })),
    result: z.object({
        hide: z.optional(z.boolean()),
        attributes: z.optional(z.object({})),
    }),
})

export const QuestionnaireElementSchema = z.union([
    z.object({
        background: z.optional(z.string()),
        borderRadius: z.optional(z.string()),
        color: z.optional(z.string()),
        padding: z.optional(z.string()),
        fontSize: z.optional(z.string()),
        margin: z.optional(z.string()),
        element: z.enum(['text']),
        text: z.optional(z.string()),
        align: z.optional(z.enum(['center', 'right'])),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
    }),
    z.object({
        element: z.enum(['error']),
        text: z.optional(z.string()),
        margin: z.optional(z.string()),
        padding: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        variant: z.optional(z.enum(['group'])),
        name: z.string(),
    }),
    z.object({
        align: z.optional(z.enum(['center', 'right', 'left'])),
        clearAfter: z.optional(z.boolean()),
        element: z.enum(['button']),
        variant: z.optional(z.enum(['primary'])),
        text: z.optional(z.string()),
        target: z.optional(z.string()),
        textAlign: z.optional(z.enum(['center'])),
        inline: z.optional(z.boolean()),
        iconLeft: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
        iconRight: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
        href: z.optional(z.string()),
        title: z.optional(z.string()),
        maxWidth: z.optional(z.string()),
        padding: z.optional(z.string()),
        margin: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        validateRequired: z.optional(z.boolean()),
    }),
    z.object({
        align: z.optional(z.enum(['center', 'right', 'left'])),
        textAlign: z.optional(z.enum(['center', 'right', 'left'])),
        element: z.enum(['queue_button']),
        text: z.optional(z.string()),
        text_closed: z.optional(z.string()),
        inline: z.optional(z.boolean()),
        queue_id: z.optional(z.string()),
        target: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        maxWidth: z.optional(z.string()),
        padding: z.optional(z.string()),
        margin: z.optional(z.string()),
        iconLeft: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
        iconRight: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
    }),
    z.object({
        element: z.enum(['textarea']),
        id: z.string(),
        name: z.string(),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        invalid: z.optional(z.boolean()),
        invalidText: z.optional(z.string()),
        showCorrectState: z.optional(z.boolean()),
        label: z.optional(z.string()),
        labelDescription: z.optional(z.string()),
        placeholder: z.optional(z.string()),
        rows: z.optional(z.number()),
        resizable: z.optional(z.boolean()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        required: z.optional(z.boolean()),
        margin: z.optional(z.string()),
        padding: z.optional(z.string()),
    }),
    z.object({
        element: z.enum(['input']),
        id: z.string(),
        name: z.string(),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        invalid: z.optional(z.boolean()),
        invalidText: z.optional(z.string()),
        showCorrectState: z.optional(z.boolean()),
        label: z.optional(z.string()),
        labelDescription: z.optional(z.string()),
        placeholder: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        required: z.optional(z.boolean()),
        margin: z.optional(z.string()),
        padding: z.optional(z.string()),
    }),
    z.object({
        element: z.enum(['select']),
        id: z.string(),
        name: z.string(),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        invalid: z.optional(z.boolean()),
        invalidText: z.optional(z.string()),
        label: z.optional(z.string()),
        labelDescription: z.optional(z.string()),
        placeholder: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        required: z.optional(z.boolean()),
        items: z.array(z.object({
            text: z.string(),
            value: z.string(),
        })),
        margin: z.optional(z.string()),
        padding: z.optional(z.string()),
    }),
    z.object({
        element: z.enum(['checkbox']),
        id: z.string(),
        name: z.string(),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        text: z.optional(z.string()),
        description: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        selected: z.optional(z.boolean()),
        errorId: z.optional(z.string()),
        margin: z.optional(z.string()),
    }),
    z.object({
        element: z.enum(['switch']),
        id: z.string(),
        name: z.string(),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        text: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        selected: z.optional(z.boolean()),
        margin: z.optional(z.string()),
    }),
    z.object({
        align: z.optional(z.enum(['center', 'right', 'left'])),
        textAlign: z.optional(z.enum(['center', 'right', 'left'])),
        element: z.enum(['radio']),
        iconLeft: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
        iconRight: z.optional(z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        })),
        id: z.string(),
        clearAfter: z.optional(z.boolean()),
        text: z.optional(z.string()),
        name: z.optional(z.string()),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        value: z.string(),
        inline: z.optional(z.boolean()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        target: z.optional(z.string()),
        errorId: z.optional(z.string()),
        maxWidth: z.optional(z.string()),
        padding: z.optional(z.string()),
        margin: z.optional(z.string()),
    }),
    z.object({
        element: z.enum(['sso']),
        margin: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        accessType: z.optional(z.enum(['session', 'channel', 'identity_verify', 'identity_auth_reset'])),
        accessUrl: z.optional(z.string()),
        ssoName: z.optional(z.string()),
        showDelegateAuthentication: z.optional(z.boolean()),
        showAuthentication: z.optional(z.boolean()),
    }),
    z.object({
        align: z.optional(z.enum(['center', 'right'])),
        element: z.enum(['rating']),
        name: z.string(),
        margin: z.optional(z.string()),
        groupName: z.optional(z.string()),
        readableName: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
        target: z.optional(z.string()),
        type: z.enum(['emoji-5', 'emoji-3', 'nps']),
        npsDetails: z.optional(z.object({
            numberOfItems: z.optional(z.number()),
            startingValue: z.optional(z.number()),
            topLabel: z.optional(z.string()),
            bottomLabel: z.optional(z.string()),
        })),
        icons: z.optional(z.object({
            good: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
                svg: z.optional(z.string()),
            })),
            averageGood: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
                svg: z.optional(z.string()),
            })),
            average: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
                svg: z.optional(z.string()),
            })),
            averageBad: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
                svg: z.optional(z.string()),
            })),
            bad: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
                svg: z.optional(z.string()),
            })),
        })),
        reverse: z.optional(z.boolean()),
    }),
    z.object({
        element: z.enum(['attachment']),
        name: z.optional(z.string()),
        margin: z.optional(z.string()),
        logic: z.optional(QuestionnaireElementLogic),
        hide: z.optional(z.boolean()),
    }),
])

export const QuestionnaireItems = z.array(
    z.union([
        z.object({
            id: z.optional(z.string()),
            type: z.enum(['view']),
            elements: z.optional(z.array(
                QuestionnaireElementSchema,
            )),
            navigation: z.optional(z.object({
                enableMobileStacking: z.optional(z.boolean()),
                fixed: z.optional(z.boolean()),
                position: z.optional(z.enum(['top', 'bottom'])),
                elements: z.optional(z.array(
                    QuestionnaireElementSchema,
                )),
            })),
        }),
        z.object({
            id: z.optional(z.string()),
            type: z.enum(['logic-queues-closed']),
            queues: z.optional(z.array(z.string())),
            result: z.object({
                target_success: z.optional(z.string()),
                target_fail: z.optional(z.string()),
            }),
        }),
        z.object({
            id: z.optional(z.string()),
            type: z.enum(['logic']),
            conditions: QuestionnaireElementLogic.shape.conditions,
            result: z.object({
                message: z.optional(z.string()),
                userName: z.optional(z.string()),
                queueId: z.optional(z.string()),
                target_success: z.optional(z.string()),
                target_fail: z.optional(z.string()),
            }),
        }),
        z.object({
            id: z.optional(z.string()),
            type: z.enum(['redirect']),
            target: z.optional(z.string()),
        }),
    ]),
)

// This schema defines allowed embed theme overrides
export const ThemeSchema = z.object({
    backgroundSizes: z.optional(z.object({
        questionnaire: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        chatBacklog: z.optional(z.string()),
    })),
    customFonts: z.optional(z.array(
        z.object({
            family: z.string(),
            weight: z.optional(z.union([z.string(), z.number()])),
            style: z.optional(z.enum(['normal', 'italic', 'bold'])),
            src: z.string(),
            ascentOverride: z.optional(z.string()),
            descentOverride: z.optional(z.string()),
            lineGapOverride: z.optional(z.string()),
        }),
    )),
    colors: z.optional(z.object({
        // button filled - primary
        buttonFilledBackground: z.optional(z.string()),
        buttonFilledBackgroundActive: z.optional(z.string()),
        buttonFilledBackgroundDisabled: z.optional(z.string()),
        buttonFilledBackgroundFocus: z.optional(z.string()),
        buttonFilledBackgroundHover: z.optional(z.string()),
        buttonFilledBorder: z.optional(z.string()),
        buttonFilledBorderActive: z.optional(z.string()),
        buttonFilledBorderDisabled: z.optional(z.string()),
        buttonFilledBorderFocus: z.optional(z.string()),
        buttonFilledBorderHover: z.optional(z.string()),
        buttonFilledText: z.optional(z.string()),
        buttonFilledTextActive: z.optional(z.string()),
        buttonFilledTextdDisabled: z.optional(z.string()),
        buttonFilledTextFocus: z.optional(z.string()),
        buttonFilledTextHover: z.optional(z.string()),
        buttonFilledIcon: z.optional(z.string()),
        buttonFilledIconActive: z.optional(z.string()),
        buttonFilledIconDisabled: z.optional(z.string()),
        buttonFilledIconFocus: z.optional(z.string()),
        buttonFilledIconHover: z.optional(z.string()),
        buttonFilledOutlineFocus: z.optional(z.string()),

        // button default - secondary
        buttonBackground: z.optional(z.string()),
        buttonBackgroundActive: z.optional(z.string()),
        buttonBackgroundDisabled: z.optional(z.string()),
        buttonBackgroundFocus: z.optional(z.string()),
        buttonBackgroundHover: z.optional(z.string()),
        buttonBorder: z.optional(z.string()),
        buttonBorderActive: z.optional(z.string()),
        buttonBorderDisabled: z.optional(z.string()),
        buttonBorderFocus: z.optional(z.string()),
        buttonBorderHover: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonTextActive: z.optional(z.string()),
        buttonTextDisabled: z.optional(z.string()),
        buttonTextFocus: z.optional(z.string()),
        buttonTextHover: z.optional(z.string()),
        buttonIcon: z.optional(z.string()),
        buttonIconActive: z.optional(z.string()),
        buttonIconDisabled: z.optional(z.string()),
        buttonIconFocus: z.optional(z.string()),
        buttonIconHover: z.optional(z.string()),
        buttonOutlineFocus: z.optional(z.string()),

        // button text - tertiary
        buttonTextBackground: z.optional(z.string()),
        buttonTextBackgroundActive: z.optional(z.string()),
        buttonTextBackgroundDisabled: z.optional(z.string()),
        buttonTextBackgroundFocus: z.optional(z.string()),
        buttonTextBackgroundHover: z.optional(z.string()),
        buttonTextBorder: z.optional(z.string()),
        buttonTextBorderActive: z.optional(z.string()),
        buttonTextBorderDisabled: z.optional(z.string()),
        buttonTextBorderFocus: z.optional(z.string()),
        buttonTextBorderHover: z.optional(z.string()),
        buttonTextContentText: z.optional(z.string()),
        buttonTextContentTextActive: z.optional(z.string()),
        buttonTextContentTextDisabled: z.optional(z.string()),
        buttonTextContentTextFocus: z.optional(z.string()),
        buttonTextContentTextHover: z.optional(z.string()),
        buttonTextIcon: z.optional(z.string()),
        buttonTextIconActive: z.optional(z.string()),
        buttonTextIconDisabled: z.optional(z.string()),
        buttonTextIconFocus: z.optional(z.string()),
        buttonTextIconHover: z.optional(z.string()),
        buttonTextOutlineFocus: z.optional(z.string()),

        // button embed send
        buttonSendBackground: z.optional(z.string()),
        buttonSendBackgroundActive: z.optional(z.string()),
        buttonSendBackgroundDisabled: z.optional(z.string()),
        buttonSendBackgroundFocus: z.optional(z.string()),
        buttonSendBackgroundHover: z.optional(z.string()),
        buttonSendBorder: z.optional(z.string()),
        buttonSendBorderActive: z.optional(z.string()),
        buttonSendBorderDisabled: z.optional(z.string()),
        buttonSendBorderFocus: z.optional(z.string()),
        buttonSendBorderHover: z.optional(z.string()),
        buttonSendText: z.optional(z.string()),
        buttonSendTextActive: z.optional(z.string()),
        buttonSendTextdDisabled: z.optional(z.string()),
        buttonSendTextFocus: z.optional(z.string()),
        buttonSendTextHover: z.optional(z.string()),
        buttonSendIcon: z.optional(z.string()),
        buttonSendIconActive: z.optional(z.string()),
        buttonSendIconDisabled: z.optional(z.string()),
        buttonSendIconFocus: z.optional(z.string()),
        buttonSendIconHover: z.optional(z.string()),
        buttonSendOutlineFocus: z.optional(z.string()),

        // command bar buttons
        buttonCommandBarBorder: z.optional(z.string()),
        buttonCommandBarBorderActive: z.optional(z.string()),
        buttonCommandBarBorderDisabled: z.optional(z.string()),
        buttonCommandBarBorderFocus: z.optional(z.string()),
        buttonCommandBarBorderHover: z.optional(z.string()),

        // button video
        buttonVideoBackground: z.optional(z.string()),
        buttonVideoBackgroundActive: z.optional(z.string()),
        buttonVideoBackgroundDisabled: z.optional(z.string()),
        buttonVideoBackgroundFocus: z.optional(z.string()),
        buttonVideoBackgroundHover: z.optional(z.string()),
        buttonVideoBorder: z.optional(z.string()),
        buttonVideoBorderActive: z.optional(z.string()),
        buttonVideoBorderDisabled: z.optional(z.string()),
        buttonVideoBorderFocus: z.optional(z.string()),
        buttonVideoBorderHover: z.optional(z.string()),
        buttonVideoText: z.optional(z.string()),
        buttonVideoTextActive: z.optional(z.string()),
        buttonVideoTextdDisabled: z.optional(z.string()),
        buttonVideoTextFocus: z.optional(z.string()),
        buttonVideoTextHover: z.optional(z.string()),
        buttonVideoIcon: z.optional(z.string()),
        buttonVideoIconActive: z.optional(z.string()),
        buttonVideoIconDisabled: z.optional(z.string()),
        buttonVideoIconFocus: z.optional(z.string()),
        buttonVideoIconHover: z.optional(z.string()),
        buttonVideoOutlineFocus: z.optional(z.string()),

        // radio
        questionnaireRadioBackground: z.optional(z.string()),
        questionnaireRadioBackgroundSelected: z.optional(z.string()),
        questionnaireRadioBackgroundHover: z.optional(z.string()),
        questionnaireRadioBorder: z.optional(z.string()),
        questionnaireRadioBorderSelected: z.optional(z.string()),
        questionnaireRadioBorderHover: z.optional(z.string()),
        questionnaireRadioText: z.optional(z.string()),
        questionnaireRadioTextSelected: z.optional(z.string()),
        questionnaireRadioTextHover: z.optional(z.string()),
        questionnaireRadioFocusOutline: z.optional(z.string()),

        // modal
        modalBackground: z.optional(z.string()),
        modalHeaderBackground: z.optional(z.string()),
        modalFooterBackground: z.optional(z.string()),
        modalFooterBorder: z.optional(z.string()),
        modalText: z.optional(z.string()),
        modalBorder: z.optional(z.string()),

        // input
        inputBackground: z.optional(z.string()),
        inputText: z.optional(z.string()),
        inputBorder: z.optional(z.string()),
        inputBackgroundDisabled: z.optional(z.string()),
        inputTextDisabled: z.optional(z.string()),
        inputBorderDisabled: z.optional(z.string()),
        inputBackgroundHover: z.optional(z.string()),
        inputTextHover: z.optional(z.string()),
        inputBorderHover: z.optional(z.string()),
        inputBackgroundActive: z.optional(z.string()),
        inputTextActive: z.optional(z.string()),
        inputBorderActive: z.optional(z.string()),
        inputBackgroundFocus: z.optional(z.string()),
        inputTextFocus: z.optional(z.string()),
        inputBorderFocus: z.optional(z.string()),
        inputBackgroundInvalid: z.optional(z.string()),
        inputTextInvalid: z.optional(z.string()),
        inputBorderInvalid: z.optional(z.string()),
        inputBackgroundValid: z.optional(z.string()),
        inputTextValid: z.optional(z.string()),
        inputBorderValid: z.optional(z.string()),
        inputBackgroundAutopopulated: z.optional(z.string()),
        inputTextAutopopulated: z.optional(z.string()),
        inputBorderAutopopulated: z.optional(z.string()),
        inputBackgroundAutopopulatedFocus: z.optional(z.string()),
        inputTextAutopopulatedFocus: z.optional(z.string()),
        inputBorderAutopopulatedFocus: z.optional(z.string()),
        inputPlaceholder: z.optional(z.string()),
        inputIconColor: z.optional(z.string()),
        inputErrorMessage: z.optional(z.string()),

        // toast
        toastBackgroundDefault: z.optional(z.string()),
        toastTextDefault: z.optional(z.string()),
        toastBorderDefault: z.optional(z.string()),
        toastBackgroundSuccess: z.optional(z.string()),
        toastTextSuccess: z.optional(z.string()),
        toastBorderSuccess: z.optional(z.string()),
        toastBackgroundError: z.optional(z.string()),
        toastTextError: z.optional(z.string()),
        toastBorderError: z.optional(z.string()),

        // checkbox
        checkboxBackground: z.optional(z.string()),
        checkboxBackgroundDisabled: z.optional(z.string()),
        checkboxBackgroundHover: z.optional(z.string()),
        checkboxBackgroundFocus: z.optional(z.string()),

        checkboxBackgroundChecked: z.optional(z.string()),
        checkboxBackgroundCheckedDisabled: z.optional(z.string()),
        checkboxBackgroundCheckedHover: z.optional(z.string()),
        checkboxBackgroundCheckedFocus: z.optional(z.string()),

        checkboxBorder: z.optional(z.string()),
        checkboxBorderDisabled: z.optional(z.string()),
        checkboxBorderHover: z.optional(z.string()),
        checkboxBorderFocus: z.optional(z.string()),

        checkboxBorderChecked: z.optional(z.string()),
        checkboxBorderCheckedDisabled: z.optional(z.string()),
        checkboxBorderCheckedHover: z.optional(z.string()),
        checkboxBorderCheckedFocus: z.optional(z.string()),

        checkboxIconChecked: z.optional(z.string()),
        checkboxIconCheckedDisabled: z.optional(z.string()),
        checkboxIconCheckedHover: z.optional(z.string()),
        checkboxIconCheckedFocus: z.optional(z.string()),

        checkboxBackgroundHoverChecked: z.optional(z.string()),
        checkboxDescription: z.optional(z.string()),
        checkboxFocusOutline: z.optional(z.string()),

        // switch
        switchBackground: z.optional(z.string()),
        switchBackgroundHover: z.optional(z.string()),
        switchBackgroundFocus: z.optional(z.string()),
        switchBackgroundDisabled: z.optional(z.string()),

        switchBackgroundChecked: z.optional(z.string()),
        switchBackgroundCheckedDisabled: z.optional(z.string()),
        switchBackgroundCheckedHover: z.optional(z.string()),
        switchBackgroundCheckedFocus: z.optional(z.string()),

        switchBorder: z.optional(z.string()),
        switchBorderHover: z.optional(z.string()),
        switchBorderFocus: z.optional(z.string()),
        switchBorderDisabled: z.optional(z.string()),

        switchBorderChecked: z.optional(z.string()),
        switchBorderCheckedHover: z.optional(z.string()),
        switchBorderCheckedFocus: z.optional(z.string()),
        switchBorderCheckedDisabled: z.optional(z.string()),

        switchThumbBackground: z.optional(z.string()),
        switchThumbBackgroundDisabled: z.optional(z.string()),
        switchThumbBackgroundHover: z.optional(z.string()),
        switchThumbBackgroundFocus: z.optional(z.string()),

        switchThumbBackgroundChecked: z.optional(z.string()),
        switchThumbBackgroundCheckedDisabled: z.optional(z.string()),
        switchThumbBackgroundCheckedHover: z.optional(z.string()),
        switchThumbBackgroundCheckedFocus: z.optional(z.string()),

        switchThumbBorder: z.optional(z.string()),
        switchThumbBorderDisabled: z.optional(z.string()),
        switchThumbBorderHover: z.optional(z.string()),
        switchThumbBorderFocus: z.optional(z.string()),

        switchThumbBorderChecked: z.optional(z.string()),
        switchThumbBorderCheckedDisabled: z.optional(z.string()),
        switchThumbBorderCheckedHover: z.optional(z.string()),
        switchThumsBorderCheckedFocus: z.optional(z.string()),

        switchIcon: z.optional(z.string()),
        switchIconDisabled: z.optional(z.string()),
        switchIconHover: z.optional(z.string()),
        switchIconFocus: z.optional(z.string()),
        switchIconChecked: z.optional(z.string()),

        switchOutlineFocus: z.optional(z.string()),

        // floating
        floatingEmbedHeader: z.optional(z.string()),
        floatingEmbedHeaderBorder: z.optional(z.string()),
        floatingEmbedHeaderTitle: z.optional(z.string()),
        floatingEmbedHeaderIcon: z.optional(z.string()),
        floatingEmbedHeaderSubtitle: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedHeaderButtonIcon: z.optional(z.string()),
        floatingEmbedHeaderButtonHover: z.optional(z.string()),
        floatingEmbedHeaderButtonIconHover: z.optional(z.string()),
        floatingEmbedHeaderButtonBackground: z.optional(z.string()),
        floatingEmbedHeaderButtonBorder: z.optional(z.string()),
        floatingEmbedHeaderButtonHoverBackground: z.optional(z.string()),
        floatingEmbedHeaderButtonFocusBackground: z.optional(z.string()),
        floatingEmbedHeaderButtonActiveOutline: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonTitle: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonSubtitle: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonTitleHover: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonSubtitleHover: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonIcon: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonIconHover: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonBackground: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonBorder: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonHoverBackground: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonFocusBackground: z.optional(z.string()),
        floatingEmbedHeaderTitleButtonActiveOutline: z.optional(z.string()),
        floatingEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonIcon: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonHover: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonIconHover: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonBackground: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonBorder: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonHoverBackground: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonFocusBackground: z.optional(z.string()),
        floatingEmbedHeaderCloseButtonActiveOutline: z.optional(z.string()),
        floatingEmbedHeaderAvatar: z.optional(z.string()),
        floatingEmbedHeaderAvatarBackground: z.optional(z.string()),
        floatingEmbedHeaderAvatarBorder: z.optional(z.string()),

        // ball
        floatingEmbedBall: z.optional(z.string()),
        floatingEmbedBallHover: z.optional(z.string()),
        floatingEmbedBallActive: z.optional(z.string()),
        floatingEmbedBallText: z.optional(z.string()),
        floatingEmbedBallTextHover: z.optional(z.string()),

        // bubble
        floatingEmbedPromoBubble: z.optional(z.string()),
        floatingEmbedPromoBubbleText: z.optional(z.string()),

        // container
        containerEmbedHeader: z.optional(z.string()),
        containerEmbedHeaderBorder: z.optional(z.string()),
        containerEmbedHeaderTitle: z.optional(z.string()),
        containerEmbedHeaderIcon: z.optional(z.string()),
        containerEmbedHeaderSubtitle: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderButtonIcon: z.optional(z.string()),
        containerEmbedHeaderButtonHover: z.optional(z.string()),
        containerEmbedHeaderButtonIconHover: z.optional(z.string()),
        containerEmbedHeaderButtonBackground: z.optional(z.string()),
        containerEmbedHeaderButtonBorder: z.optional(z.string()),
        containerEmbedHeaderButtonHoverBackground: z.optional(z.string()),
        containerEmbedHeaderButtonFocusBackground: z.optional(z.string()),
        containerEmbedHeaderButtonActiveOutline: z.optional(z.string()),
        containerEmbedHeaderTitleButtonTitle: z.optional(z.string()),
        containerEmbedHeaderTitleButtonSubtitle: z.optional(z.string()),
        containerEmbedHeaderTitleButtonTitleHover: z.optional(z.string()),
        containerEmbedHeaderTitleButtonSubtitleHover: z.optional(z.string()),
        containerEmbedHeaderTitleButtonBackground: z.optional(z.string()),
        containerEmbedHeaderTitleButtonBorder: z.optional(z.string()),
        containerEmbedHeaderTitleButtonHoverBackground: z.optional(z.string()),
        containerEmbedHeaderTitleButtonFocusBackground: z.optional(z.string()),
        containerEmbedHeaderTitleButtonActiveOutline: z.optional(z.string()),
        containerEmbedHeaderCloseButton: z.optional(z.string()),
        containerEmbedHeaderCloseButtonIcon: z.optional(z.string()),
        containerEmbedHeaderCloseButtonHover: z.optional(z.string()),
        containerEmbedHeaderCloseButtonIconHover: z.optional(z.string()),
        containerEmbedHeaderCloseButtonBackground: z.optional(z.string()),
        containerEmbedHeaderCloseButtonBorder: z.optional(z.string()),
        containerEmbedHeaderCloseButtonHoverBackground: z.optional(z.string()),
        containerEmbedHeaderCloseButtonFocusBackground: z.optional(z.string()),
        containerEmbedHeaderCloseButtonActiveOutline: z.optional(z.string()),
        containerEmbedHeaderAvatar: z.optional(z.string()),
        containerEmbedHeaderAvatarBackground: z.optional(z.string()),
        containerEmbedHeaderAvatarBorder: z.optional(z.string()),

        // iframe body
        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedBodyBold: z.optional(z.string()),
        embedBodyBackground: z.optional(z.string()),
        embedErrorTitle: z.optional(z.string()),
        embedBodyHighlighted: z.optional(z.string()),
        embedModalHeader: z.optional(z.string()),
        embedModalIcon: z.optional(z.string()),

        // hero
        embedHero: z.optional(z.string()),
        embedHeroBorder: z.optional(z.string()),
        embedHeroBackground: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroAvatarBackground: z.optional(z.string()),
        embedHeroAvatarBorder: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),
        embedQueuePosition: z.optional(z.string()),
        embedQueueSpinner: z.optional(z.string()),

        // questionnaire
        questionnaireBackground: z.optional(z.string()),
        questionnaireNavigation: z.optional(z.string()),
        questionnaireNavigationBorder: z.optional(z.string()),
        questionnaireEmojiGood: z.optional(z.string()),
        questionnaireEmojiAverageGood: z.optional(z.string()),
        questionnaireEmojiAverage: z.optional(z.string()),
        questionnaireEmojiAverageBad: z.optional(z.string()),
        questionnaireEmojiBad: z.optional(z.string()),

        questionnaireLightBotBubbleBackground: z.optional(z.string()),
        questionnaireLightBotBubbleBorder: z.optional(z.string()),
        questionnaireLightBotBubbleText: z.optional(z.string()),
        questionnaireLightBotUsername: z.optional(z.string()),

        questionnaireLightBotAnswerBubbleBackground: z.optional(z.string()),
        questionnaireLightBotAnswerBubbleBorder: z.optional(z.string()),
        questionnaireLightBotAnswerBubbleText: z.optional(z.string()),
        questionnaireLightBotAnswerBubbleLabel: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),
        questionnaireLinkHover: z.optional(z.string()),

        questionnaireLabel: z.optional(z.string()),
        questionnaireLabelDescription: z.optional(z.string()),

        // chat
        chatBacklogBackground: z.optional(z.string()),
        chatCommandBarWrapper: z.optional(z.string()),
        chatCommandBar: z.optional(z.string()),
        chatCommandBarBorder: z.optional(z.string()),
        chatCommandBarTextareaText: z.optional(z.string()),
        chatCommandBarTextareaPlaceholder: z.optional(z.string()),

        messageInfo: z.optional(z.string()),
        messageInfoBorder: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageInfoIconSuccess: z.optional(z.string()),
        messageInfoTimestamp: z.optional(z.string()),
        messageInfoIcon: z.optional(z.string()),
        messageInfoLink: z.optional(z.string()),
        messageInfoLinkHover: z.optional(z.string()),
        messageAvatarOperatorModeratorBorder: z.optional(z.string()),
        messageAvatarOperatorModeratorText: z.optional(z.string()),
        messageAvatarOtherBorder: z.optional(z.string()),
        messageAvatarOtherText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModeratorBorder: z.optional(z.string()),
        messageSpeechBubbleOtherBorder: z.optional(z.string()),

        messageSpeechBubbleOperatorModeratorText: z.optional(z.string()),
        messageSpeechBubbleOtherText: z.optional(z.string()),

        messageSpeechBubbleOperatorModeratorLink: z.optional(z.string()),
        messageSpeechBubbleOperatorModeratorLinkHover: z.optional(z.string()),
        messageSpeechBubbleOtherLink: z.optional(z.string()),
        messageSpeechBubbleOtherLinkHover: z.optional(z.string()),

        messageSpeechBubbleOperatorModeratorTimestamp: z.optional(z.string()),
        messageSpeechBubbleOtherTimestamp: z.optional(z.string()),

        skeletonLoader: z.optional(z.string()),
        skeletonLoaderBackground: z.optional(z.string()),
        skeletonLoaderProgress: z.optional(z.string()),
    })),
    borderStyles: z.optional(z.object({
        embedHero: z.optional(z.string()),
        floatingEmbedHeaderBorder: z.optional(z.string()),
        containerEmbedHeaderBorder: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderTitleButton: z.optional(z.string()),
        floatingEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeaderCloseButton: z.optional(z.string()),
        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonCommandBar: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),
        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        checkbox: z.optional(z.string()),
        switch: z.optional(z.string()),
        switchThumb: z.optional(z.string()),
        chatCommandBar: z.optional(z.string()),
        messageInfo: z.optional(z.string()),
        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),
        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
        questionnaireNavigation: z.optional(z.string()),
    })),
    borderWidths: z.optional(z.object({
        embedHero: z.optional(z.string()),
        floatingEmbedHeaderBorder: z.optional(z.string()),
        containerEmbedHeaderBorder: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderTitleButton: z.optional(z.string()),
        floatingEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeaderCloseButton: z.optional(z.string()),
        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonCommandBar: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),
        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        checkbox: z.optional(z.string()),
        switch: z.optional(z.string()),
        switchThumb: z.optional(z.string()),
        chatCommandBar: z.optional(z.string()),
        messageInfo: z.optional(z.string()),
        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),
        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
        questionnaireNavigation: z.optional(z.string()),
    })),
    fonts: z.optional(z.object({
        floatingEmbedBall: z.optional(z.string()),
        floatingEmbedHeaderTitle: z.optional(z.string()),
        floatingEmbedHeaderSubtitle: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedPromoBubble: z.optional(z.string()),

        // container
        containerEmbedHeaderTitle: z.optional(z.string()),
        containerEmbedHeaderSubtitle: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),

        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
    })),
    fontSizes: z.optional(z.object({
        floatingEmbedBall: z.optional(z.string()),
        floatingEmbedBallState: z.optional(z.string()),
        floatingEmbedHeaderTitle: z.optional(z.string()),
        floatingEmbedHeaderSubtitle: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedPromoBubble: z.optional(z.string()),
        floatingEmbedHeaderInQueueLoader: z.optional(z.string()),
        floatingEmbedHeaderAvatar: z.optional(z.string()),

        // container
        containerEmbedHeaderTitle: z.optional(z.string()),
        containerEmbedHeaderSubtitle: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderAvatar: z.optional(z.string()),

        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        embedQueuePosition: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),
        questionnaireLabel: z.optional(z.string()),
        questionnaireLabelDescription: z.optional(z.string()),

        questionnaireLightBotUsername: z.optional(z.string()),
        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        skeletonLoader: z.optional(z.string()),
    })),
    fontStyle: z.optional(z.object({
        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),
        questionnaireLabel: z.optional(z.string()),
        questionnaireLabelDescription: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
    })),
    fontVariant: z.optional(z.object({
        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),
        questionnaireLabel: z.optional(z.string()),
        questionnaireLabelDescription: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
    })),
    fontWeights: z.optional(z.object({
        floatingEmbedBall: z.optional(z.number()),
        floatingEmbedHeaderTitle: z.optional(z.string()),
        floatingEmbedHeaderSubtitle: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),

        // container
        containerEmbedHeaderTitle: z.optional(z.string()),
        containerEmbedHeaderSubtitle: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),

        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        questionnaireLink: z.optional(z.string()),
        questionnaireLabel: z.optional(z.string()),
        questionnaireLabelDescription: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),

        skeletonLoader: z.optional(z.string()),
    })),
    maxWidths: z.optional(z.object({
        questionnaire: z.optional(z.string()),
        questionnaireNavigation: z.optional(z.string()),
    })),
    space: z.optional(z.object({
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonSendMargin: z.optional(z.string()),
        buttonCommandBar: z.optional(z.string()),
        buttonCommandBarMargin: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),
        inQueueText: z.optional(z.string()),
        input: z.optional(z.string()),
        inputModal: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeader: z.optional(z.string()),
        containerEmbedHeader: z.optional(z.string()),
        floatingEmbedBall: z.optional(z.string()),
        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),
    })),
    zIndices: z.optional(z.object({
        floatingEmbed: z.optional(z.number()),
        floatingEmbedBall: z.optional(z.number()),
        containerHeader: z.optional(z.number()),
    })),
    radii: z.optional(z.object({
        floatingEmbed: z.optional(z.string()),
        floatingEmbedBall: z.optional(z.string()),
        floatingEmbedPromoBubble: z.optional(z.string()),
        floatingEmbedHeaderButton: z.optional(z.string()),
        containerEmbedHeaderButton: z.optional(z.string()),
        floatingEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderTitleButton: z.optional(z.string()),
        containerEmbedHeaderCloseButton: z.optional(z.string()),
        floatingEmbedHeaderCloseButton: z.optional(z.string()),

        embedHero: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonCommandBar: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        checkbox: z.optional(z.string()),
        switch: z.optional(z.string()),
        switchThumb: z.optional(z.string()),
        chatCommandBar: z.optional(z.string()),
        messageInfo: z.optional(z.string()),

        messageSpeechBubbleOther: z.optional(z.string()),
        messageSpeechBubbleOperatorModerator: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
    })),
    shadows: z.optional(z.object({
        floatingEmbed: z.optional(z.string()),
        floatingEmbedBall: z.optional(z.string()),
        floatingEmbedPromoBubble: z.optional(z.string()),
    })),
    lineHeights: z.optional(z.object({
        embedBody: z.optional(z.string()),
        embedBodyHeading1: z.optional(z.string()),
        embedBodyHeading2: z.optional(z.string()),
        embedBodyHeading3: z.optional(z.string()),
        embedBodyHeading4: z.optional(z.string()),
        embedBodyHeading5: z.optional(z.string()),
        embedBodyHeading6: z.optional(z.string()),
        embedHero: z.optional(z.string()),
        embedHeroTitle: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        embedHeroTitleInQueue: z.optional(z.string()),
        embedHeroSubtitle: z.optional(z.string()),
        embedHeroSubtitleInQueue: z.optional(z.string()),
        embedHeroErrorTitle: z.optional(z.string()),
        embedHeroErrorMessage: z.optional(z.string()),

        embedModalHeader: z.optional(z.string()),

        buttonFilled: z.optional(z.string()),
        button: z.optional(z.string()),
        buttonText: z.optional(z.string()),
        buttonSend: z.optional(z.string()),
        buttonVideo: z.optional(z.string()),

        modal: z.optional(z.string()),
        input: z.optional(z.string()),
        toast: z.optional(z.string()),
        messageInfoText: z.optional(z.string()),
        messageUsernameOperatorModerator: z.optional(z.string()),
        messageUsernameOther: z.optional(z.string()),

        messageSpeechBubbleOperatorModerator: z.optional(z.string()),
        messageSpeechBubbleOther: z.optional(z.string()),

        questionnaireLightBotBubble: z.optional(z.string()),
        questionnaireLightBotAnswerBubble: z.optional(z.string()),
    })),
    sizes: z.optional(z.object({
        floatingEmbedHeaderInQueueLoader: z.optional(z.string()),
        floatingEmbedHeaderVideoIcon: z.optional(z.string()),
        floatingEmbedHeaderNewMessageIcon: z.optional(z.string()),
        floatingEmbedBallInQueueLoader: z.optional(z.string()),
        floatingEmbedBallVideoIcon: z.optional(z.string()),
        floatingEmbedBallNewMessageIcon: z.optional(z.string()),
        floatingEmbedHeaderAvatar: z.optional(z.string()),
        containerEmbedHeaderAvatar: z.optional(z.string()),
        embedQueueSpinner: z.optional(z.string()),
        embedHeroAvatar: z.optional(z.string()),
        chatTextareaMaxHeight: z.optional(z.string()),
    })),
})

export const EnvironmentSchema = z.object({
    audienceMetadata: z.optional(z.any({})),
    scheduleAccessKey: z.optional(z.string()),
    channelId: z.optional(z.string()),
    askUserName: z.optional(z.boolean()),
    audienceAutoQueue: z.optional(z.string()),
    audienceRealmId: z.string(),
    autoStart: z.optional(z.boolean()),
    showCloseConfirmation: z.optional(z.boolean()),
    closeConfirmText: z.optional(z.string()),
    closeConfirmTitle: z.optional(z.string()),
    closeConfirmQueueText: z.optional(z.string()),
    closeConfirmQueueTitle: z.optional(z.string()),
    closeConfirmQuestionnaireText: z.optional(z.string()),
    closeConfirmQuestionnaireTitle: z.optional(z.string()),
    audienceStartedText: z.optional(z.string()),
    // Replaces the agent's real display name everywhere it is shown to the audience
    agentName: z.optional(z.string()),
    // Overrides the agent's job title shown to the audience. An empty string hides the job title
    agentJobTitle: z.optional(z.string()),
    agentAvatar: z.optional(z.object({
        src: z.optional(z.string()),
        svg: z.optional(z.string()),
        alt: z.optional(z.string()),
    })),
    messageAvatars: z.optional(z.enum(['all', 'agentsOnly', 'none'])),
    useSkeletonLoader: z.optional(z.boolean()),
    reverseEmbedHeroContentOrdering: z.optional(z.boolean()),
    chatThumbnailsSize: z.object({
        maxWidth: z.optional(z.string()),
        maxHeight: z.optional(z.string()),
    }),
    sendText: z.optional(z.string()),
    sendIcon: z.optional(z.union([
        z.object({
            src: z.optional(z.string()),
            svg: z.optional(z.string()),
        }),
        z.null(),
    ])),
    lightBotAvatarIcon: z.optional(z.object({
        src: z.optional(z.string()),
        svg: z.optional(z.string()),
        alt: z.optional(z.string()),
        width: z.optional(z.number()),
        height: z.optional(z.number()),
    })),
    lightBotAvatarName: z.optional(z.string()),
    containerOptions: z.optional(z.object({
        showHeader: z.optional(z.boolean()),
        headerOptions: z.optional(z.object({
            height: z.optional(z.number()),
            title: z.optional(z.string()),
            image: z.optional(z.object({
                src: z.optional(z.string()),
                svg: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
            })),
            dropdownOptions: z.optional(z.array(z.union([
                z.object({name: z.enum(['mute', 'chatList', 'close', 'minimize', 'expand'])}),
                z.object({
                    name: z.string(), // custom identifier passed to onSelect and emitted on click
                    label: z.optional(z.string()),
                    icon: z.optional(z.object({
                        src: z.optional(z.string()),
                        svg: z.optional(z.string()),
                        alt: z.optional(z.string()),
                        width: z.optional(z.number()),
                        height: z.optional(z.number()),
                    })),
                    // Close the dropdown after this item is selected (default false)
                    closeDropdownAfterSelect: z.optional(z.boolean()),
                    // Invoked in the host page when the custom item is clicked. Functions are not passed into the iframe
                    onSelect: z.optional(z.function()),
                }),
            ]))),
            actions: z.optional(z.object({
                close: z.optional(z.object({
                    icon: ActionIconSchema,
                    buttonTitle: z.optional(z.string()),
                    text: z.optional(z.string()),
                })),
                dropdown: z.optional(z.object({
                    icon: ActionIconSchema,
                    buttonTitle: z.optional(z.string()),
                    text: z.optional(z.string()),
                })),
                chatList: z.optional(z.object({
                    icon: ActionIconSchema,
                    buttonTitle: z.optional(z.string()),
                    text: z.optional(z.string()),
                })),
            })),
        })),
    })),
    floating: z.optional(z.boolean()),
    floatingOptions: z.optional(z.object({
        ball: z.optional(z.boolean()),
        ballOptions: z.optional(z.object({
            close: z.optional(z.boolean()),
            text: z.optional(z.string()),
            image: z.optional(z.object({
                src: z.optional(z.string()),
                alt: z.optional(z.string()),
                svg: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
            })),
            height: z.optional(z.number()),
            offset: z.optional(z.object({
                x: z.optional(z.number()),
                y: z.optional(z.number()),
            })),
            width: z.optional(z.number()),
        })),
        headerOptions: z.optional(z.object({
            height: z.optional(z.number()),
            title: z.optional(z.object({
                minimized: z.optional(z.string()),
                full: z.optional(z.string()),
                open: z.optional(z.string()),
            })),
            image: z.optional(z.object({
                src: z.optional(z.string()),
                svg: z.optional(z.string()),
                alt: z.optional(z.string()),
                width: z.optional(z.number()),
                height: z.optional(z.number()),
            })),
            dropdownOptions: z.optional(z.array(z.union([
                z.object({name: z.enum(['mute', 'chatList', 'close', 'minimize', 'expand'])}),
                z.object({
                    name: z.string(), // custom identifier passed to onSelect and emitted on click
                    label: z.optional(z.string()),
                    icon: z.optional(z.object({
                        src: z.optional(z.string()),
                        svg: z.optional(z.string()),
                        alt: z.optional(z.string()),
                        width: z.optional(z.number()),
                        height: z.optional(z.number()),
                    })),
                    // Close the dropdown after this item is selected (default false)
                    closeDropdownAfterSelect: z.optional(z.boolean()),
                    // Invoked in the host page when the custom item is clicked. Functions are not passed into the iframe
                    onSelect: z.optional(z.function()),
                }),
            ]))),
            actions: z.optional(z.object({
                minimized: z.optional(z.object({
                    minimize: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    expand: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    close: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    dropdown: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    chatList: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                })),
                open: z.optional(z.object({
                    minimize: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    expand: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    close: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    dropdown: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    chatList: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                })),
                full: z.optional(z.object({
                    minimize: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    expand: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    close: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    dropdown: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                    chatList: z.optional(z.object({
                        icon: ActionIconSchema,
                        buttonTitle: z.optional(z.string()),
                        text: z.optional(z.string()),
                    })),
                })),
            })),
        })),
        promoBubble: z.optional(z.object({
            caret: z.optional(z.boolean()),
            showTimeout: z.optional(z.number()),
            offset: z.optional(z.object({
                x: z.optional(z.number()),
                y: z.optional(z.number()),
            })),
            items: z.optional(z.array(
                z.object({content: z.optional(z.string())}),
            )),
        })),
        dock: z.optional(z.object({
            bottom: z.optional(z.boolean()),
            center: z.optional(z.boolean()),
            left: z.optional(z.boolean()),
            right: z.optional(z.boolean()),
        })),
        height: z.optional(z.string()),
        width: z.optional(z.object({
            minimized: z.optional(z.string()),
            full: z.optional(z.string()),
            open: z.optional(z.string()),
        })),
        initialState: z.optional(z.enum(['minimized', 'open', 'full'])),
        offset: z.optional(z.object({
            x: z.optional(z.number()),
            y: z.optional(z.number()),
        })),
    })),
    inQueueText: z.optional(z.string()),
    conversationEndedButtonText: z.optional(z.string()),
    initialRoute: z.optional(InitialRouteSchema),
    language: z.optional(z.enum(['fi', 'en', 'sv'])),
    remember: z.optional(z.boolean()),
    sessionKey: z.optional(z.string()),
    lightBot: z.optional(z.boolean()),
    sso: z.optional(SSOSchema),
    newSso: z.optional(z.boolean()),
    // eslint-disable-next-line no-extra-parens
    theme: z.optional(ThemeSchema as NinchatEnvironmentConfigThemeRT),
    colorScheme: z.optional(z.enum(['light', 'dark'])),
    translations: z.optional(z.record(z.string(), z.object({}))),
    hideSidebar: z.optional(z.boolean()),
    hideMenubar: z.optional(z.boolean()),
    timeFormat: z.optional(z.enum(['12', '24'])),
    timeLocale: z.optional(z.string()),
    muteSounds: z.optional(z.boolean()),
    sounds: z.optional(z.object({
        messageReceived: z.optional(z.union([z.string(), z.boolean()])),
        messageReceivedMinimized: z.optional(z.union([z.string(), z.boolean()])),
        messageSent: z.optional(z.union([z.string(), z.boolean()])),
        messageSentMinimized: z.optional(z.union([z.string(), z.boolean()])),
        audienceStarted: z.optional(z.union([z.string(), z.boolean()])),
        audienceStartedMinimized: z.optional(z.union([z.string(), z.boolean()])),
        audienceTransferred: z.optional(z.union([z.string(), z.boolean()])),
        audienceTransferredMinimized: z.optional(z.union([z.string(), z.boolean()])),
        queuePositionChanged: z.optional(z.union([z.string(), z.boolean()])),
        queuePositionChangedMinimized: z.optional(z.union([z.string(), z.boolean()])),
        queued: z.optional(z.union([z.string(), z.boolean()])),
        queuedMinimized: z.optional(z.union([z.string(), z.boolean()])),
        audienceEnded: z.optional(z.union([z.string(), z.boolean()])),
        audienceEndedMinimized: z.optional(z.union([z.string(), z.boolean()])),
        promoBubble: z.optional(z.union([z.string(), z.boolean()])),
    })),
    variant: z.union([
        z.literal('app'),
        z.literal('queue'),
        z.literal('channel'),
    ]),
    responsive: z.optional(
        z.array(z.object({
            minWidth: z.number(),
            maxWidth: z.number(),
            overrides: z.object({}),
        })),
    ),
    questionnaireValues: z.optional(z.object({})),
    preAudienceQuestionnaire: QuestionnaireItems as NinchatEnvironmentConfigQuestionnaireItemsRT,
    postAudienceQuestionnaire: QuestionnaireItems as NinchatEnvironmentConfigQuestionnaireItemsRT,
})

export const ConfigKeySchema = z.string().regex(/^site\/[a-z0-9]+\/[a-z0-9]+.json$/um)

export const EnvironmentRecordSchema: z.ZodRecord<z.ZodString, typeof EnvironmentSchema> = z.record(z.string(), EnvironmentSchema)

export const SiteConfigSchema: z.ZodObject<{
    default: typeof EnvironmentSchema;
    description: z.ZodString;
}> = z.object({
    default: EnvironmentSchema,
    description: z.string(),
}).catchall(EnvironmentRecordSchema)
